import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getBreakpointUp,
  getFontCss,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFilteredPeopleInvitedToOrg,
  selectNumberOfInvitedToOrgFromState,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.selectors'
import { PeopleMembersAnalyticsEventProperties } from 'src/app/PeopleMembers/PeopleMembers'
import { createGetOrganizationMembersInitAction } from 'src/app/PeopleMembers/redux/peopleMembers.actions'
import {
  selectFilteredPeopleMembers,
  selectNumberOfMembersFromState,
} from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import Box from 'src/components/Box/Box'
import PeopleInvitedToOrgDataGrid from 'src/components/DataGrid/tables/PeopleInvitedToOrgDataGrid/PeopleInvitedToOrgDataGrid'
import PeopleMembersDataGrid from 'src/components/DataGrid/tables/PeopleMembersDataGrid/PeopleMembersDataGrid'
import FilterSection from 'src/components/FilterSection/FilterSection'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import {
  PeopleFiltersBody,
  PeopleInfiniteScrollProps,
} from 'src/components/PeopleBoxAndLinks/PeopleBoxAndLinks'
import TestId from 'src/components/TestId/TestId'
import { OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch, useDebounced } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { peopleInvitedToOrgUrl, peopleMembersUrl } from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { SearchQueryParams } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectIsLeftFlyoutOpen,
  selectIsRightFlyoutOpen,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'
import { InfiniteScrollProps } from 'src/types'
import { setEquality } from 'src/utils/set.utils'
import { css } from 'styled-components'

interface PeopleProps {
  peopleInfiniteScrollProps: PeopleInfiniteScrollProps
  loading?: boolean
  showFilters?: boolean
  stateQueryParams: SearchQueryParams
  setStateQueryParams: (SearchQueryParams: SearchQueryParams) => void
  keywordFilter?: string
  setKeywordFilter?: (keyword: string) => void
  organizationRolesFilter?: Set<string>
  setOrganizationRoleFilter?: (organizationRoles: Set<string>) => void
  handleOrganizationRoleSelection?: (role: string) => void
  hideDeactivatedUsers?: boolean
  setHideDeactivatedUsers?: (hideDeactivatedUsers: boolean) => void
  setSort: (sortField: string) => void
  sortBy?: string
  sortDescending?: boolean
  initialLoad: boolean
  setInitialLoad: (initialLoad: boolean) => void
  clientHeight?: number
  setSelectedFilters?: (
    selectedFilters: PeopleMembersAnalyticsEventProperties,
  ) => void
}

const PeopleBox: React.FC<PeopleProps> = (props) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const numberOfOrgMembers = useSelector(selectNumberOfMembersFromState)
  const numberOfInvitedPeople = useSelector(selectNumberOfInvitedToOrgFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const memberListModels = useSelector(selectFilteredPeopleMembers)
  const invitedListModels = useSelector(selectFilteredPeopleInvitedToOrg)
  const queryParams = useSelector(selectQueryParams)
  const brandColorCta = useSelector(selectBrandColorCta)
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: currentOrganization?.id,
  })

  const translate = useTranslate()
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()

  // prevents loading indicator from showing when page is in the background
  const [loading, setLoading] = useState<boolean>(!!props.loading)
  const [pushNewSearchState, setPushNewSearchState] = useState<boolean>(false)
  const [initialRender, setInitialRender] = useState<boolean>(true)
  const [roleFilterSelected, setRoleFilterSelected] = useState<boolean>(false)

  useEffect(() => {
    if (isRightFlyoutOpen) {
      setLoading(false)
    } else {
      setLoading(!!props.loading)
    }
  }, [isRightFlyoutOpen, props.loading])

  useEffect(() => {
    if (!isRightFlyoutOpen) {
      props.setStateQueryParams(queryParams)
    }
  }, [queryString.stringify(queryParams)])

  const peopleList = (): React.ReactNode => {
    const gridInfiniteScrollProps: InfiniteScrollProps<PeopleFiltersBody> = {
      ...props.peopleInfiniteScrollProps,
      fetch: (body, page): void =>
        props.peopleInfiniteScrollProps.fetch(body ?? {}, page),
    }
    const sortableProps = {
      setSort: props.setSort,
      sortBy: props.sortBy,
      sortDescending: props.sortDescending,
    }
    switch (calculatedBackgroundLocation.pathname) {
      case peopleInvitedToOrgUrl():
        return (
          <PeopleInvitedToOrgDataGrid
            invites={Object.values(invitedListModels)}
            clientHeight={props.clientHeight}
            infiniteScrollProps={gridInfiniteScrollProps}
            filtersBody={filtersBody}
            sortableProps={sortableProps}
          />
        )
      case peopleMembersUrl():
      default:
        return (
          <PeopleMembersDataGrid
            people={Object.values(memberListModels)}
            clientHeight={props.clientHeight}
            infiniteScrollProps={gridInfiniteScrollProps}
            filtersBody={filtersBody}
            sortableProps={sortableProps}
          />
        )
    }
  }

  const sectionCount = (): React.ReactNode => {
    switch (calculatedBackgroundLocation.pathname) {
      case peopleMembersUrl():
      default:
        return numberOfOrgMembers === 1 ? (
          isFiltered ? (
            translate('${numberOfResults} Result', {
              numberOfResults: numberOfOrgMembers.toString(),
            })
          ) : (
            translate('${numberOfOrgMembers} Active Org Member', {
              numberOfOrgMembers: numberOfOrgMembers.toString(),
            })
          )
        ) : isFiltered ? (
          translate('${numberOfResults} Results', {
            numberOfResults: numberOfOrgMembers.toString(),
          })
        ) : loading ? (
          <Processing className={'processing'} />
        ) : (
          translate(
            props.hideDeactivatedUsers
              ? '${numberOfOrgMembers} Active Org Members'
              : '${numberOfOrgMembers} Org Members',
            {
              numberOfOrgMembers: numberOfOrgMembers.toString(),
            },
          )
        )
      case peopleInvitedToOrgUrl():
        return numberOfInvitedPeople === 1
          ? isFiltered
            ? translate('${numberOfResults} Result', {
                numberOfResults: numberOfInvitedPeople.toString(),
              })
            : translate('${numInvited} Invited Person', {
                numInvited: numberOfInvitedPeople.toString(),
              })
          : isFiltered
            ? translate('${numberOfResults} Results', {
                numberOfResults: numberOfInvitedPeople.toString(),
              })
            : translate('${numInvited} Invited People', {
                numInvited: numberOfInvitedPeople.toString(),
              })
    }
  }

  const handleSelection = (): void => {
    // call init action to set loading state immediately
    dispatch(createGetOrganizationMembersInitAction())
    pushUrlStateDebounce()
  }

  const [isFiltered, setIsFiltered] = useState(false)

  const filtersBody: PeopleFiltersBody = {
    search: props.keywordFilter ? `${props.keywordFilter}` : undefined,
    organizationRoles: props.organizationRolesFilter
      ? (Array.from(props.organizationRolesFilter) as OrganizationRoles[])
      : undefined,
    showDeactivatedUsers: !props.hideDeactivatedUsers,
    sortField: props.sortBy ? props.sortBy : undefined,
    sortDirection: props.sortDescending === false ? 'ASC' : 'DESC',
  }

  const { debouncedF } = useDebounced((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      props.peopleInfiniteScrollProps.fetch(filtersBody)
    }
  })

  const pushUrlStateDebounce = useCallback(
    _.debounce(() => {
      setPushNewSearchState(true)
    }, 500),
    [],
  )

  // The filter params PeopleBox owns. Single source of truth: `paramString` is
  // built from this, and buildSearchString() derives the keys to strip from it,
  // so the two can never drift (a new filter key here can't double-encode).
  const ownedFilterParams = {
    search: filtersBody.search,
    organizationRoles: filtersBody.organizationRoles,
    showDeactivatedUsers: filtersBody.showDeactivatedUsers,
  }

  const paramString = queryString.stringify(ownedFilterParams)

  // PeopleBox writes the URL via raw window.history, so it owns the entire query
  // string. Rebuilding the search from filter keys alone would clobber params
  // this component does not own — notably `bulkInvite`, which deep-links the
  // bulk-invite modal and would otherwise be stripped the moment a filter sync
  // ran, silently closing the modal. Merge the filter params with any preserved
  // unrelated params instead of replacing the whole search.
  const buildSearchString = (): string => {
    const preserved = new URLSearchParams(window.location.search)
    Object.keys(ownedFilterParams).forEach((key) => preserved.delete(key))
    const preservedString = preserved.toString()
    const combined = [paramString, preservedString].filter(Boolean).join('&')
    return combined.length > 0 ? `?${combined}` : location.pathname
  }

  const pushUrlState = (): void => {
    window.history.pushState(JSON.stringify({}), '', buildSearchString())
  }

  const replaceUrlState = (): void => {
    window.history.replaceState(JSON.stringify({}), '', buildSearchString())
  }

  // restores query params on flyout close
  useEffect((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      replaceUrlState()
    }
  }, [isRightFlyoutOpen, isLeftFlyoutOpen])

  useEffect((): void => {
    if (initialRender) {
      setInitialRender(false)
    } else {
      // Only push a filter change when the value actually differs. Re-setting an
      // identical value (e.g. a fresh Set with the same roles) on flyout close
      // would retrigger the filter effect below and refetch page 0, collapsing
      // the accumulated infinite-scroll pages back to the first page.
      const nextKeyword = props?.stateQueryParams?.search || ''
      if (props.setKeywordFilter && props.keywordFilter !== nextKeyword) {
        props.setKeywordFilter(nextKeyword)
      }
      const nextRoles = new Set<string>(
        props?.stateQueryParams?.organizationRoles,
      )
      if (
        props.setOrganizationRoleFilter &&
        !setEquality(
          props.organizationRolesFilter || new Set<string>(),
          nextRoles,
        )
      ) {
        props.setOrganizationRoleFilter(nextRoles)
      }
      // `showDeactivatedUsers` is the URL's *show* flag — a string ('true' /
      // 'false') that's absent when defaulted. Hide is its inverse, defaulting
      // to hide=true when the param is absent (matching initialFilterValues).
      // The previous `!!showDeactivatedUsers` both read the raw string ('false'
      // is truthy) and skipped the inversion, so a non-filter query change —
      // e.g. opening the bulk-invite modal via `?bulkInvite=1` — flipped this
      // filter and forced a spurious refetch of the People table.
      const nextHideDeactivated =
        props?.stateQueryParams?.showDeactivatedUsers !== 'true'
      if (
        props.setHideDeactivatedUsers &&
        props.hideDeactivatedUsers !== nextHideDeactivated
      ) {
        props.setHideDeactivatedUsers(nextHideDeactivated)
      }
    }
  }, [props?.stateQueryParams])

  useEffect((): void => {
    if (pushNewSearchState) {
      pushUrlState()
      setPushNewSearchState(false)
    }
  }, [pushNewSearchState])

  useEffect((): void => {
    if (!props.initialLoad) {
      debouncedF()
      props.setSelectedFilters &&
        props.setSelectedFilters({
          organizationRoles:
            props.organizationRolesFilter !== undefined
              ? Array.from(props.organizationRolesFilter)
              : [],
          showDeactivatedUsers: !props.hideDeactivatedUsers,
        })
    }
    props.setInitialLoad(false)
  }, [
    props.keywordFilter,
    props.organizationRolesFilter,
    props.hideDeactivatedUsers,
    props.sortBy,
    props.sortDescending,
  ])

  const keywordFilterAnalytics = (val: string): void => {
    switch (calculatedBackgroundLocation.pathname) {
      case peopleMembersUrl():
      default:
        analytics?.track('people members - search org members - typing', {
          screen: peopleMembersUrl(),
          intent: 'search organization members',
          interaction: 'typing',
          search: val,
          results: numberOfOrgMembers,
        })
        break
      case peopleInvitedToOrgUrl():
        analytics?.track('people invited - search invited to org - typing', {
          screen: peopleInvitedToOrgUrl(),
          intent: 'search invited to org',
          interaction: 'typing',
          search: val,
          results: numberOfInvitedPeople,
        })
    }
  }

  useEffect(() => {
    setIsFiltered(
      (props.organizationRolesFilter &&
        props.organizationRolesFilter.size > 0) ||
        (props.keywordFilter !== undefined && props.keywordFilter.length > 0),
    )
  }, [props.organizationRolesFilter, props.keywordFilter])

  useEffect(() => {
    if (
      roleFilterSelected &&
      props.organizationRolesFilter &&
      props.organizationRolesFilter.size > 0
    ) {
      // assumes this functionality is only on the members tab
      analytics?.track('organization members - filter applied', {
        screen: peopleMembersUrl(),
        values: Array.from(props.organizationRolesFilter).map((role) =>
          role.toLowerCase(),
        ),
      })
      setRoleFilterSelected(false)
    }
  }, [roleFilterSelected])

  const resetFilterValues = useMemo(
    () => ({
      keyword: '',
      organizationRolesFilter: new Set<string>(),
      hideDeactivatedUsers: true,
    }),
    [],
  )

  const onFiltersResetClick = (): void => {
    props.setKeywordFilter && props.setKeywordFilter(resetFilterValues.keyword)
    props.setOrganizationRoleFilter &&
      props.setOrganizationRoleFilter(resetFilterValues.organizationRolesFilter)
    props.setHideDeactivatedUsers &&
      props.setHideDeactivatedUsers(resetFilterValues.hideDeactivatedUsers)
    setIsFiltered(false)
    pushUrlStateDebounce()
  }

  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (
      !setEquality(
        props.organizationRolesFilter || new Set<string>(),
        resetFilterValues.organizationRolesFilter,
      )
    ) {
      appliedCount++
    }
    if (props.hideDeactivatedUsers !== resetFilterValues.hideDeactivatedUsers) {
      appliedCount++
    }
    return appliedCount
  }

  return (
    <Box
      noPadding
      noShadow
      outlineBorder
      css={css`
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      `}
    >
      {calculatedBackgroundLocation.pathname === peopleMembersUrl() ? (
        <FilterSection
          appliedFiltersCount={filtersAppliedCount()}
          buttonVariant="toolbarIcon"
          headerHeight={72}
          topLeftContent={
            <div
              css={css`
                display: flex;
                width: 100%;
                align-items: center;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  flex-direction: column;
                `)}
              `}
            >
              <div
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )}
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.regular,
                    )};
                    padding: 20px 20px 0px;
                    width: 100%;
                  `)}
                `}
              >
                {sectionCount()}
              </div>
              <div
                css={css`
                  flex-grow: 1;
                  padding-left: 40px;
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    display: flex;
                    padding: 18px 20px 20px;
                    width: 100%;
                  `)}
                `}
              >
                <KeywordFilter
                  css={css`
                    ${getBreakpointDown(breakpointSizes.XSM)(css`
                      width: 100%;
                    `)}
                  `}
                  key={'KeywordFilter'}
                  value={props.keywordFilter || ''}
                  onChange={(val: string): void => {
                    keywordFilterAnalytics(val)
                    props.setKeywordFilter && props.setKeywordFilter(val)
                    pushUrlStateDebounce()
                  }}
                  showLabel={false}
                  placeholder={translateSingleString(
                    locale,
                    'Enter a name or email address',
                  )}
                />
              </div>
            </div>
          }
          hideButtonChevron
          filtersFlyoutProps={{
            title: translate('People Filters'),
            onReset: onFiltersResetClick,
            loading: props.peopleInfiniteScrollProps.loading,
            filters: (
              <>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                  `}
                >
                  <div
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {translate('Role')}
                  </div>
                  <Checkbox
                    label={translate('Owner')}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.owner,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.owner,
                          )
                        : false
                    }
                  />
                  <Checkbox
                    label={translate('Admin')}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.admin,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.admin,
                          )
                        : false
                    }
                  />
                  {orgFeatureFlags?.cardManagerRoleEnabled && (
                    <Checkbox
                      label={translate('Card Manager')}
                      onClick={(): void => {
                        props.handleOrganizationRoleSelection &&
                          props.handleOrganizationRoleSelection(
                            OrganizationRoles.cardManager,
                          )
                        handleSelection()
                      }}
                      isSelected={
                        props.organizationRolesFilter
                          ? props.organizationRolesFilter?.has(
                              OrganizationRoles.cardManager,
                            )
                          : false
                      }
                    />
                  )}
                  <Checkbox
                    label={translate('Bookkeeper')}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.bookkeeper,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.bookkeeper,
                          )
                        : false
                    }
                  />
                  <Checkbox
                    label={translate('Member')}
                    onClick={(): void => {
                      props.handleOrganizationRoleSelection &&
                        props.handleOrganizationRoleSelection(
                          OrganizationRoles.member,
                        )
                      handleSelection()
                    }}
                    isSelected={
                      props.organizationRolesFilter
                        ? props.organizationRolesFilter?.has(
                            OrganizationRoles.member,
                          )
                        : false
                    }
                  />
                  {orgFeatureFlags?.guestRoleEnabled && (
                    <Checkbox
                      label={translate('Guest')}
                      onClick={(): void => {
                        props.handleOrganizationRoleSelection &&
                          props.handleOrganizationRoleSelection(
                            OrganizationRoles.guest,
                          )
                        handleSelection()
                      }}
                      isSelected={
                        props.organizationRolesFilter
                          ? props.organizationRolesFilter?.has(
                              OrganizationRoles.guest,
                            )
                          : false
                      }
                    />
                  )}
                  <div
                    css={css`
                      margin-top: 32px;
                      display: flex;
                      align-items: flex-start;
                      justify-content: space-between;
                      gap: 8px;
                    `}
                  >
                    <span
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {translate('Hide deactivated users')}
                    </span>
                    <TestId testId={'hideDeactivatedUsers'}>
                      <OnOffSwitch
                        onBgColor={brandColorCta}
                        smallMode
                        defaultOnOffState={props.hideDeactivatedUsers}
                        onChange={(): void => {
                          props.setHideDeactivatedUsers &&
                            props.setHideDeactivatedUsers(
                              !props.hideDeactivatedUsers,
                            )
                          handleSelection()
                        }}
                      />
                    </TestId>
                  </div>
                </div>
              </>
            ),
          }}
        />
      ) : (
        <div
          css={css`
            display: flex;
            align-items: center;
            padding: 0 40px;
            ${getBreakpointUp(breakpointSizes.SM)(css`
              height: 72px;
            `)}
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              flex-direction: column;
              padding: 0;
            `)}
          `}
        >
          <span
            css={css`
              white-space: nowrap;
              ${getFontCss(
                20,
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )}
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                ${getFontCss(
                  16,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )}
                padding: 20px 20px 0px;
                width: 100%;
                white-space: normal;
              `)}
            `}
          >
            {sectionCount()}
          </span>
          <div
            css={css`
              flex-grow: 1;
              padding-left: 40px;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                display: flex;
                padding: 18px 20px 20px;
                width: 100%;
              `)}
            `}
          >
            <KeywordFilter
              css={css`
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  width: 100%;
                `)}
              `}
              key={'KeywordFilter'}
              value={props.keywordFilter || ''}
              onChange={(val: string): void => {
                keywordFilterAnalytics(val)
                props.setKeywordFilter && props.setKeywordFilter(val)
                pushUrlStateDebounce()
              }}
              showLabel={false}
              placeholder={translateSingleString(
                locale,
                'Enter a name or email address',
              )}
            />
          </div>
        </div>
      )}
      {peopleList()}
    </Box>
  )
}

export default PeopleBox
